import type { PayoutBatchesTranslations } from "@/shared/lang/interfaces/payoutBatches";

import type { BatchRailDestination } from "../types/payoutBatches.types";

const HIDDEN = "····";

const tail = (value: string, keep = 4): string => {
  const digits = value.replace(/\s+/g, "");
  return digits.length > keep ? `${HIDDEN} ${digits.slice(-keep)}` : HIDDEN;
};

const maskAccountNumber = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 10) return tail(digits);
  return `${digits.slice(0, 4)} ${HIDDEN} ${HIDDEN} ${digits.slice(-4)}`;
};

const maskKey = (type: string, value: string): string => {
  switch (type.toUpperCase()) {
    case "EMAIL": {
      const at = value.indexOf("@");
      return at > 0 ? `${value[0]}···${value.slice(at)}` : HIDDEN;
    }
    case "CPF":
    case "CNPJ":
    case "PHONE":
    case "DOCUMENT":
    case "NIT":
    case "CC":
      return tail(value.replace(/\D/g, ""));
    case "ALPHANUMERIC":
    case "MERCHANT":
      return value.length > 5 ? `${value.slice(0, 2)}···${value.slice(-3)}` : HIDDEN;
    default:
      return tail(value);
  }
};

/**
 * On-screen mask of a row's destination — the full value is only ever in the row drawer, on purpose. Bank
 * accounts keep their head (bank prefix) and tail; keys keep what identifies the kind of key.
 */
export function maskBatchDestination(destination: BatchRailDestination | null): string {
  if (!destination) return HIDDEN;
  switch (destination.rail) {
    case "SPEI":
      return maskAccountNumber(destination.clabe);
    case "CBU_CVU":
      return maskAccountNumber(destination.accountIdValue);
    case "PIX":
      return maskKey(destination.pixKeyType, destination.pixKeyValue);
    case "BREB":
      return maskKey(destination.brebKeyType, destination.brebKeyValue);
    case "TRANSFIYA":
    case "PSE":
      return `${destination.bankCode} · ${tail(destination.accountNumber)}`;
    case "USDT":
      return destination.destinationAddr.length > 10
        ? `${destination.destinationAddr.slice(0, 6)}…${destination.destinationAddr.slice(-4)}`
        : HIDDEN;
    default:
      return HIDDEN;
  }
}

/** The raw value, for the drawer and the copy action. */
export function batchDestinationValue(destination: BatchRailDestination | null): string {
  if (!destination) return "";
  switch (destination.rail) {
    case "SPEI":
      return destination.clabe;
    case "CBU_CVU":
      return destination.accountIdValue;
    case "PIX":
      return destination.pixKeyValue;
    case "BREB":
      return destination.brebKeyValue;
    case "TRANSFIYA":
    case "PSE":
      return destination.accountNumber;
    case "USDT":
      return destination.destinationAddr;
    default:
      return "";
  }
}

export interface DestinationLabel {
  key: keyof PayoutBatchesTranslations;
  params?: Record<string, string>;
}

/** Per-rail i18n key (+ interpolation) for the destination row label. */
export function batchDestinationLabel(destination: BatchRailDestination | null): DestinationLabel {
  if (!destination) return { key: "destination_label_unknown" };
  switch (destination.rail) {
    case "SPEI":
      return { key: "destination_label_spei" };
    case "CBU_CVU":
      return {
        key: "destination_label_cbu_cvu",
        params: { type: destination.accountIdType },
      };
    case "PIX":
      return {
        key: "destination_label_pix",
        params: { type: destination.pixKeyType },
      };
    case "BREB":
      return {
        key: "destination_label_breb",
        params: { type: destination.brebKeyType },
      };
    case "TRANSFIYA":
      return {
        key: "destination_label_transfiya",
        params: { type: destination.accountType },
      };
    case "PSE":
      return {
        key: "destination_label_pse",
        params: { type: destination.accountType },
      };
    case "USDT":
      return {
        key: "destination_label_usdt",
        params: { type: destination.network },
      };
    default:
      return { key: "destination_label_unknown" };
  }
}

export function batchDestinationBeneficiary(destination: BatchRailDestination | null): string | null {
  if (!destination) return null;
  if ("beneficiaryName" in destination && destination.beneficiaryName) return destination.beneficiaryName;
  return null;
}
